// ---------------------------------------------------------------------------
// webhooks.ts -- Verify signed inbound mutation webhooks.
//
// The Bounded realtime worker fires outbound webhooks for declared
// collections after a mutation commits, signed with a platform Ed25519 private
// key. This module verifies those signatures on the receiving server.
//
// verifyWebhook():
//   1. Fetches the platform's hosted public keys (well-known endpoint),
//      cached in-memory with a TTL.
//   2. Picks the key matching the X-Bounded-Key-Id header.
//   3. Verifies the Ed25519 signature (X-Bounded-Signature) over the RAW body.
//   4. Checks the X-Bounded-Timestamp is within an allowed skew.
//   5. Rejects exact signed-delivery replays within the skew window.
//   6. Returns the parsed, typed payload — or throws.
//
// Verification uses WebCrypto (globalThis.crypto.subtle), available natively in
// Node 18+, Bun, Deno, Cloudflare Workers, and modern browsers.
// ---------------------------------------------------------------------------
import { getWebhookKeysUrl, getConfiguredAppId } from "@bounded-sh/core";
// Default to PRODUCTION keys so an omitted keysUrl is production-safe (audit
// SDK LOW-7). Staging receivers must pass keysUrl explicitly.
export const DEFAULT_WEBHOOK_KEYS_URL = "https://realtime.bounded.sh/.well-known/bounded-webhook-keys.json";
const DEFAULT_MAX_SKEW_SECONDS = 300;
const DEFAULT_CACHE_TTL_MS = 300000;
// Negative-cache TTL for unknown key ids (audit U-053): bounds how often a
// forged X-Bounded-Key-Id can force a refetch of the hosted keys endpoint. A
// genuinely rotated key waits at most one TTL before a refresh is retried.
const DEFAULT_UNKNOWN_KEY_TTL_MS = 60000;
const UNKNOWN_KEY_CACHE_MAX = 256;
export class WebhookVerificationError extends Error {
    constructor(message) {
        super(message);
        this.name = "WebhookVerificationError";
    }
}
function getHeader(headers, name) {
    var _a;
    if (headers && typeof headers.get === "function") {
        return headers.get(name);
    }
    const lower = name.toLowerCase();
    const obj = headers;
    for (const key of Object.keys(obj)) {
        if (key.toLowerCase() === lower) {
            const value = obj[key];
            return Array.isArray(value) ? (_a = value[0]) !== null && _a !== void 0 ? _a : null : value !== null && value !== void 0 ? value : null;
        }
    }
    return null;
}
// ---------------------------------------------------------------------------
// Base64
// ---------------------------------------------------------------------------
function base64ToBytes(b64) {
    const binary = typeof Buffer !== "undefined"
        ? Buffer.from(b64, "base64").toString("binary")
        : atob(b64);
    const buffer = new ArrayBuffer(binary.length);
    const bytes = new Uint8Array(buffer);
    for (let i = 0; i < binary.length; i++)
        bytes[i] = binary.charCodeAt(i);
    return bytes;
}
function utf8Bytes(text) {
    const encoded = new TextEncoder().encode(text);
    const buffer = new ArrayBuffer(encoded.length);
    const bytes = new Uint8Array(buffer);
    bytes.set(encoded);
    return bytes;
}
const keyCache = new Map();
// ---------------------------------------------------------------------------
// Negative cache for unknown key ids (audit U-053)
//
// Maps `${keysUrl}\n${keyId}` -> the epoch ms until which a forced refresh for
// that key id is suppressed. Without it, every request carrying an unknown
// X-Bounded-Key-Id would evict the key cache and force one fetch of the hosted
// keys endpoint — N forged calls = N upstream fetches (refetch DoS). With it,
// a repeated unknown key id costs at most one refresh per TTL window, while a
// genuinely rotated key waits at most one TTL. Bounded size; when full, the
// oldest-inserted entry is evicted (LRU-ish; entries are re-inserted on
// update, so Map insertion order approximates recency).
// ---------------------------------------------------------------------------
const unknownKeyCache = new Map();
function recordUnknownKey(cacheKey, notFoundUntil) {
    unknownKeyCache.delete(cacheKey);
    unknownKeyCache.set(cacheKey, notFoundUntil);
    if (unknownKeyCache.size > UNKNOWN_KEY_CACHE_MAX) {
        const oldest = unknownKeyCache.keys().next().value;
        if (oldest !== undefined)
            unknownKeyCache.delete(oldest);
    }
}
/** Clear the in-memory key caches, including the unknown-key negative cache (mainly for tests). */
export function clearWebhookKeyCache() {
    keyCache.clear();
    unknownKeyCache.clear();
}
async function loadKeys(keysUrl, cacheTtlMs, fetchImpl, now) {
    const cached = keyCache.get(keysUrl);
    if (cached && cached.expiresAt > now()) {
        return cached.keys;
    }
    let res;
    try {
        res = await fetchImpl(keysUrl);
    }
    catch (err) {
        throw new WebhookVerificationError(`Failed to fetch webhook keys from ${keysUrl}: ${err.message}`);
    }
    if (!res.ok) {
        throw new WebhookVerificationError(`Failed to fetch webhook keys from ${keysUrl}: HTTP ${res.status}`);
    }
    const body = (await res.json());
    if (!body || !Array.isArray(body.keys)) {
        throw new WebhookVerificationError(`Webhook keys endpoint ${keysUrl} returned an invalid payload.`);
    }
    keyCache.set(keysUrl, { keys: body.keys, expiresAt: now() + cacheTtlMs });
    return body.keys;
}
// ---------------------------------------------------------------------------
// In-memory replay store (single-process receivers)
// ---------------------------------------------------------------------------
/**
 * A simple in-memory {@link WebhookReplayStore}. Records seen event ids until
 * their skew-window expiry and lazily evicts expired entries. Suitable for a
 * single-process receiver; use a shared store (Redis, KV, a DB unique
 * constraint) for multi-instance deployments.
 */
export class InMemoryReplayStore {
    constructor() {
        this.seen = new Map();
    }
    checkAndRecord(id, expiresAtMs) {
        const now = Date.now();
        // Opportunistic eviction of expired entries to bound memory.
        if (this.seen.size > 0) {
            for (const [key, exp] of this.seen) {
                if (exp <= now)
                    this.seen.delete(key);
            }
        }
        const existing = this.seen.get(id);
        if (existing !== undefined && existing > now) {
            return true; // replay
        }
        this.seen.set(id, expiresAtMs);
        return false;
    }
    /** Clear all recorded ids (mainly for tests). */
    clear() {
        this.seen.clear();
    }
}
const defaultReplayStore = new InMemoryReplayStore();
/** Clear the default in-memory replay cache (mainly for tests). */
export function clearWebhookReplayCache() {
    defaultReplayStore.clear();
}
// ---------------------------------------------------------------------------
// verifyWebhook
// ---------------------------------------------------------------------------
/**
 * Verify a signed inbound webhook and return its typed payload.
 *
 * @param rawBody The exact raw request body string the platform signed. Must be
 *   the unparsed bytes — re-serializing parsed JSON may not match byte-for-byte.
 * @param headers The inbound request headers (a Headers instance or a plain
 *   object; case-insensitive).
 * @param opts Optional overrides (keys URL, skew, cache TTL, fetch).
 * @returns The parsed, validated {@link WebhookPayload}.
 * @throws {WebhookVerificationError} if anything fails verification.
 */
export async function verifyWebhook(rawBody, headers, opts = {}) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    // Resolution order: explicit opts.keysUrl > the configured Bounded network's
    // hosted keys (so a staging receiver verifies staging-signed deliveries
    // without passing keysUrl) > the production default (fail-closed when the
    // network is unknown — audit SDK LOW-7).
    const keysUrl = (_b = (_a = opts.keysUrl) !== null && _a !== void 0 ? _a : getWebhookKeysUrl()) !== null && _b !== void 0 ? _b : DEFAULT_WEBHOOK_KEYS_URL;
    const maxSkew = (_c = opts.maxSkewSeconds) !== null && _c !== void 0 ? _c : DEFAULT_MAX_SKEW_SECONDS;
    const cacheTtlMs = (_d = opts.cacheTtlMs) !== null && _d !== void 0 ? _d : DEFAULT_CACHE_TTL_MS;
    const unknownKeyTtlMs = (_e = opts.unknownKeyTtlMs) !== null && _e !== void 0 ? _e : DEFAULT_UNKNOWN_KEY_TTL_MS;
    const fetchImpl = (_f = opts.fetchImpl) !== null && _f !== void 0 ? _f : globalThis.fetch;
    const now = (_g = opts.now) !== null && _g !== void 0 ? _g : Date.now;
    if (typeof fetchImpl !== "function") {
        throw new WebhookVerificationError("No fetch implementation available; pass opts.fetchImpl.");
    }
    const signature = getHeader(headers, "X-Bounded-Signature");
    const keyId = getHeader(headers, "X-Bounded-Key-Id");
    const timestampHeader = getHeader(headers, "X-Bounded-Timestamp");
    if (!signature)
        throw new WebhookVerificationError("Missing X-Bounded-Signature header.");
    if (!keyId)
        throw new WebhookVerificationError("Missing X-Bounded-Key-Id header.");
    if (!timestampHeader)
        throw new WebhookVerificationError("Missing X-Bounded-Timestamp header.");
    const headerTimestamp = Number(timestampHeader);
    if (!Number.isFinite(headerTimestamp)) {
        throw new WebhookVerificationError("Invalid X-Bounded-Timestamp header.");
    }
    // Resolve the signing key by id.
    const keys = await loadKeys(keysUrl, cacheTtlMs, fetchImpl, now);
    let key = keys.find((k) => k.id === keyId);
    // Cache-miss tolerance: a freshly-rotated key may not be cached yet. Force a
    // single refresh before giving up — unless this key id recently failed a
    // refresh, in which case the negative cache (audit U-053) suppresses the
    // refetch so forged unknown key ids cannot force one upstream fetch per
    // request.
    if (!key) {
        const unknownCacheKey = `${keysUrl}\n${keyId}`;
        const notFoundUntil = unknownKeyCache.get(unknownCacheKey);
        if (notFoundUntil !== undefined && notFoundUntil > now()) {
            throw new WebhookVerificationError(`No public key found for key id "${keyId}".`);
        }
        keyCache.delete(keysUrl);
        const refreshed = await loadKeys(keysUrl, cacheTtlMs, fetchImpl, now);
        key = refreshed.find((k) => k.id === keyId);
        if (!key) {
            recordUnknownKey(unknownCacheKey, now() + unknownKeyTtlMs);
        }
    }
    if (!key) {
        throw new WebhookVerificationError(`No public key found for key id "${keyId}".`);
    }
    if (key.alg !== "ed25519") {
        throw new WebhookVerificationError(`Unsupported key algorithm "${key.alg}".`);
    }
    // Verify the Ed25519 signature over the raw body.
    const subtle = (globalThis.crypto && globalThis.crypto.subtle);
    if (!subtle) {
        throw new WebhookVerificationError("WebCrypto (crypto.subtle) is not available.");
    }
    let cryptoKey;
    try {
        cryptoKey = await subtle.importKey("raw", base64ToBytes(key.publicKey), { name: "Ed25519" }, false, ["verify"]);
    }
    catch (err) {
        throw new WebhookVerificationError(`Failed to import Ed25519 public key: ${err.message}`);
    }
    const ok = await subtle.verify({ name: "Ed25519" }, cryptoKey, base64ToBytes(signature), utf8Bytes(rawBody));
    if (!ok) {
        throw new WebhookVerificationError("Webhook signature verification failed.");
    }
    // Parse and shallow-validate the payload.
    let payload;
    try {
        payload = JSON.parse(rawBody);
    }
    catch (_j) {
        throw new WebhookVerificationError("Webhook body is not valid JSON.");
    }
    if (!payload ||
        typeof payload.id !== "string" ||
        typeof payload.appId !== "string" ||
        typeof payload.path !== "string" ||
        (payload.operation !== "create" &&
            payload.operation !== "update" &&
            payload.operation !== "delete") ||
        typeof payload.timestamp !== "number" ||
        !Number.isFinite(payload.timestamp)) {
        throw new WebhookVerificationError("Webhook payload is missing required fields.");
    }
    // Replay protection: the timestamp used for the skew window MUST be the SIGNED
    // payload.timestamp, not the unsigned X-Bounded-Timestamp header. An attacker
    // can swap the header freely without breaking the Ed25519 signature, so the
    // header alone is no replay bound. We also require the header to equal the
    // signed value so the (unsigned) header cannot diverge from what was signed.
    if (headerTimestamp !== payload.timestamp) {
        throw new WebhookVerificationError("X-Bounded-Timestamp header does not match the signed payload timestamp.");
    }
    const nowSeconds = Math.floor(now() / 1000);
    if (Math.abs(nowSeconds - payload.timestamp) > maxSkew) {
        throw new WebhookVerificationError(`Webhook timestamp outside allowed skew of ${maxSkew}s (delta ${Math.abs(nowSeconds - payload.timestamp)}s).`);
    }
    // #072: bind the delivery to an app by DEFAULT. The shared platform signing key means a
    // valid signature proves "from Bounded", not "from your app"; payload.appId is the ONLY
    // per-app separation. Previously the bind ran only when expectedAppId was passed, so the
    // secure check was opt-in and a malicious app owner could point their app's webhook at a
    // victim receiver. Now: an explicit expectedAppId is authoritative; otherwise bind to the
    // init()-time app id; a multi-app receiver must set allowAnyAppId to accept any app; and
    // when no app id is resolvable we FAIL CLOSED rather than silently trust any app.
    if (opts.expectedAppId !== undefined) {
        if (payload.appId !== opts.expectedAppId) {
            throw new WebhookVerificationError(`Webhook appId "${payload.appId}" does not match expected appId "${opts.expectedAppId}".`);
        }
    }
    else if (!opts.allowAnyAppId) {
        const configuredAppId = getConfiguredAppId();
        if (!configuredAppId) {
            throw new WebhookVerificationError("verifyWebhook cannot determine which app this webhook is for: no expectedAppId was " +
                "passed and the SDK has no init()-time app id. Pass expectedAppId, or set " +
                "allowAnyAppId: true to intentionally accept webhooks from any app.");
        }
        if (payload.appId !== configuredAppId) {
            throw new WebhookVerificationError(`Webhook appId "${payload.appId}" does not match the configured app id "${configuredAppId}". ` +
                "Pass allowAnyAppId: true to accept webhooks from any app.");
        }
    }
    // Replay cache: reject a previously-seen signed delivery within the skew
    // window. The worker's payload.id is the document id/path, so using it alone
    // would reject legitimate rapid updates to the same document.
    //
    // SHW-05: delivery is durable + at-least-once, and a RETRY of one delivery
    // uses a fresh timestamp (to stay inside the skew window) and therefore a
    // different signature. Keying replay on the signed, per-delivery `deliveryId`
    // (stable across retries) is what actually dedupes those retries; the old
    // `${keyId}:${signature}` key only caught exact byte-identical resends and
    // would wave every retry of the same delivery straight through. Fall back to
    // the signature only for a legacy payload with no deliveryId. Pair a shared
    // store (retained for the retry window) with this for exactly-once handling.
    const replayStore = opts.replayStore === undefined ? defaultReplayStore : opts.replayStore;
    if (replayStore) {
        const expiresAtMs = (payload.timestamp + maxSkew) * 1000;
        const replayKey = typeof payload.deliveryId === "string" && payload.deliveryId.length > 0
            ? `${keyId}:did:${payload.deliveryId}`
            : `${keyId}:${signature}`;
        const replayed = await replayStore.checkAndRecord(replayKey, expiresAtMs);
        if (replayed) {
            throw new WebhookVerificationError(`Webhook replay detected for event id "${payload.id}" (delivery "${(_h = payload.deliveryId) !== null && _h !== void 0 ? _h : "?"}").`);
        }
    }
    return payload;
}
//# sourceMappingURL=webhooks.js.map